import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@jeraisonne/db';
import type { ClassAssignmentSummary, AssignmentItem } from './objective-types';
import { withDoneFromAttempts } from './assignment-done';

export async function getClassAssignments(
  supabase: SupabaseClient<Database>,
  studentId: string,
): Promise<ClassAssignmentSummary[]> {
  const { data: memberships } = await supabase
    .from('class_members')
    .select(
      `
      class_id,
      classes (
        id, name,
        profiles ( display_name:visible_name )
      )
    `,
    )
    .eq('student_id', studentId)
    .eq('status', 'active');

  if (!memberships || memberships.length === 0) return [];

  const classIds = memberships.map((m) => m.class_id);

  const { data: assignments } = await supabase
    .from('assignments')
    .select(
      `
      id, exercise_id, class_id, status, due_at, assigned_at,
      exercises ( title, difficulty, estimated_minutes )
    `,
    )
    .eq('student_id', studentId)
    .in('class_id', classIds);

  const byClass = new Map<string, typeof assignments>();
  for (const a of assignments ?? []) {
    if (!a.class_id) continue;
    const list = byClass.get(a.class_id) ?? [];
    list.push(a);
    byClass.set(a.class_id, list);
  }

  // `finish_attempt` ne met pas le devoir à `completed` : on le déduit des tentatives.
  const doneIds = new Set(
    (
      await withDoneFromAttempts(
        supabase,
        studentId,
        (assignments ?? []).map((a) => ({
          id: a.id,
          exerciseId: a.exercise_id,
          status: a.status as AssignmentItem['status'],
          assignedAt: (a as { assigned_at?: string | null }).assigned_at ?? null,
        })),
      )
    )
      .filter((a) => a.status === 'completed')
      .map((a) => a.id),
  );

  return memberships.map((m) => {
    const cls = m.classes as unknown as {
      id: string;
      name: string;
      profiles: { display_name: string } | null;
    };
    const list = byClass.get(m.class_id) ?? [];

    const items: AssignmentItem[] = list.map((a) => {
      const ex = a.exercises as unknown as {
        title: string;
        difficulty: string;
        estimated_minutes: number;
      };
      return {
        id: a.id,
        exerciseId: a.exercise_id,
        exerciseTitle: ex.title,
        difficulty: ex.difficulty as AssignmentItem['difficulty'],
        estimatedMinutes: ex.estimated_minutes,
        status: doneIds.has(a.id) ? 'completed' : (a.status as AssignmentItem['status']),
        dueAt: a.due_at ?? null,
      };
    });

    return {
      classId: cls.id,
      className: cls.name,
      tutorName: cls.profiles?.display_name ?? 'Professeur',
      pendingCount: items.filter((a) => a.status === 'pending').length,
      inProgressCount: items.filter((a) => a.status === 'in_progress').length,
      completedCount: items.filter((a) => a.status === 'completed').length,
      assignments: items,
    };
  });
}
